// Kept identical in the three independently deployed signing clients.
export const hasLivenessId = (value: any) =>
  (typeof value === 'string' || typeof value === 'number') &&
  /^\d+$/.test(String(value)) &&
  Number.isSafeInteger(Number(value)) &&
  Number(value) > 0;

const sameId = (left: any, right: any) =>
  hasLivenessId(left) && hasLivenessId(right) && String(left) === String(right);

const hasManualStatus = (record: any) =>
  String(
    record?.selfieValidationtType ?? record?.selfieValidationType ?? ''
  ).toUpperCase() === 'MANUAL' &&
  record?.selfieVerificationStatus != null &&
  record.selfieVerificationStatus !== '';

const latestIdentity = (records: any[]) =>
  records.reduce((latest, record) => {
    const time = (item: any) => Date.parse(item?.transactionDate) || 0;
    return !latest ||
      time(record) > time(latest) ||
      (time(record) === time(latest) &&
        Number(record.stageCustomerIdentityId) >
          Number(latest.stageCustomerIdentityId))
      ? record
      : latest;
  }, null);

export const selectLivenessIdentity = (identities: any, agreementId: any) => {
  if (!hasLivenessId(agreementId) || !Array.isArray(identities)) return null;
  const records = identities.filter(Boolean);
  // Preserve agreement creation: prefer the current agreement, otherwise use the latest identity.
  const current = records.filter((record) =>
    sameId(record.agreementId, agreementId)
  );
  if (current.length) return latestIdentity(current);
  return latestIdentity(records);
};

export const isCallCenterRecord = (record: any, agreementId: any) =>
  sameId(record?.agreementId, agreementId) &&
  hasManualStatus(record) &&
  ['PENDING', 'APPROVED'].includes(record.selfieVerificationStatus);

export const isCallCenterAuthComplete = (record: any) =>
  record?.selfieVerificationStatus === 'APPROVED' &&
  String(record?.livenessChecked) === '1';

export const getLivenessGate = (identities: any, agreementId: any) => {
  if (
    !Array.isArray(identities) ||
    !identities.length ||
    identities.some((record) => !record || typeof record !== 'object')
  ) {
    return { record: null, step: 'LVLCHECK' };
  }
  // MANSELF (even with an agreement) and automatic NAAT have no manual selfie status.
  if (!identities.some(hasManualStatus)) return null;
  const record = selectLivenessIdentity(identities, agreementId);
  const manual = isCallCenterRecord(record, agreementId);
  // Completed approval is reusable; only a new authorization must match the current agreement.
  const complete = hasManualStatus(record)
    ? isCallCenterAuthComplete(record)
    : String(record?.livenessChecked) === '1';
  return { record, step: complete ? 'HIWK' : manual ? 'CCAUTH' : 'LVLCHECK' };
};

export const duoErrorMessage = (result: any, fallback: string) => {
  if (result?.status === 200)
    return result?.data?.response?.status_msg || fallback;
  const error = result?.data?.errors?.[0]?.error;
  return typeof error === 'string'
    ? error.split('"')[7] || error || fallback
    : fallback;
};

// Audit is nonblocking; DUO and exact persisted identity verification are mandatory.
export const authorizeManualLiveness = async (options: any, api: any) => {
  const {
    enabled,
    agreementId,
    customerId,
    stgCustomerId,
    storeNumber,
    record,
    remoteId,
    securityCode,
    isCurrent,
    fallback,
  } = options;
  if (!enabled || !isCurrent()) return null;
  if (
    ![
      agreementId,
      customerId,
      stgCustomerId,
      record?.stageCustomerIdentityId,
    ].every(hasLivenessId) ||
    !isCallCenterRecord(record, agreementId) ||
    !remoteId?.trim() ||
    !securityCode?.trim()
  ) {
    throw new Error(fallback);
  }
  let duo: any;
  // Never audit or approve until DUO explicitly allows the request.
  try {
    duo = await api.GetRemoteLoginStatus({
      userName: remoteId.trim(),
      passCode: securityCode.trim(),
      flowType: 'CCSelfieApprove',
    });
  } catch (error: any) {
    throw new Error(duoErrorMessage(error?.response, fallback));
  }
  if (!isCurrent()) return null;
  if (duo?.status !== 200 || duo?.data?.response?.result !== 'allow') {
    throw new Error(duoErrorMessage(duo, fallback));
  }
  try {
    await api.storeException({
      storeException: [
        {
          identifierId: Number(customerId),
          identifierType: 'CUSTOMER',
          storeNumber,
          exceptionTypeRefCode: 'CCLVA',
          exceptionText: `Manual selfie authorized by Call Center. Remote ID: ${remoteId.trim()}`,
          agreementId: Number(agreementId),
          approverId: remoteId.trim(),
        },
      ],
    });
  } catch (_) {
    /* Existing nonblocking audit policy. */
  }
  if (!isCurrent()) return null;
  const update = await api.ManageCustomerStgData(
    stgCustomerId,
    String(record.stageCustomerIdentityId),
    record,
    {
      agreementId: String(agreementId),
      livenessChecked: 1,
      selfieVerificationStatus: 'APPROVED',
    }
  );
  if (!isCurrent()) return null;
  if (update?.status !== 200) throw new Error(fallback);
  // Verify the exact updated identity before advancing; another approved record is not confirmation.
  const verification = await api.GetCustomerStgData(stgCustomerId);
  if (!isCurrent()) return null;
  const verified = selectLivenessIdentity(
    verification?.data?.identityInfo,
    agreementId
  );
  if (
    verification?.status !== 200 ||
    !sameId(
      verified?.stageCustomerIdentityId,
      record.stageCustomerIdentityId
    ) ||
    !isCallCenterRecord(verified, agreementId) ||
    !isCallCenterAuthComplete(verified)
  )
    throw new Error(fallback);
  return verified;
};
